<template>
  <div v-if="loadError" class="load-error-wrap">
    <ErrorState @retry="retryLoad" />
  </div>
  <div v-else class="home">
    <section
      class="hero-fullscreen"
      :class="{ 'slide-linkable': !!(slides[currentSlide] && slides[currentSlide].link && slides[currentSlide].linkType) }"
      @click="goSlide(slides[currentSlide])"
    >
      <!-- 轮播背景 -->
      <div class="hero-carousel">
        <div
          v-for="(slide, index) in slides"
          :key="index"
          class="carousel-slide"
          :class="{ active: currentSlide === index }"
          :style="{ backgroundImage: slideBg(slide) }"
        ></div>
      </div>

      <div class="hero-overlay"></div>

      <!-- 轮播内容 -->
      <div class="hero-content-center">
        <transition name="fade" mode="out-in">
          <div :key="currentSlide" class="slide-content">
            <h1 class="hero-title-large" data-aos="fade-up">{{ locale === 'en-US' ? (slides[currentSlide].titleEn || slides[currentSlide].title) : slides[currentSlide].title }}</h1>
            <p class="hero-subtitle-large" data-aos="fade-up" data-aos-delay="200">{{ locale === 'en-US' ? (slides[currentSlide].subtitleEn || slides[currentSlide].subtitle) : slides[currentSlide].subtitle }}</p>
            <p class="hero-tagline" data-aos="fade-up" data-aos-delay="300">{{ slides[currentSlide].tagline }}</p>
            <div class="hero-buttons-large" data-aos="fade-up" data-aos-delay="400">
              <button class="btn-primary-large" @click.stop="goDetail('equipment', '单束多丝')">
                <span>{{ $t('home.exploreProducts') }}</span>
              </button>
              <button class="btn-secondary-large" @click.stop="startInquiry">{{ $t('home.contactUs') }}</button>
            </div>
          </div>
        </transition>
      </div>

      <!-- 轮播指示器 -->
      <div class="carousel-indicators">
        <button
          v-for="(slide, index) in slides"
          :key="index"
          class="indicator-dot"
          :class="{ active: currentSlide === index }"
          @click.stop="goToSlide(index)"
        ></button>
      </div>

      <!-- 左右切换按钮 -->
      <button class="carousel-arrow carousel-prev" @click.stop="prevSlide">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="carousel-arrow carousel-next" @click.stop="nextSlide">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </section>

    <section class="intro-section">
      <div class="intro-container">
        <div class="intro-image" data-aos="fade-right">
          <img :src="config.home_lab_image || '/placeholder-images/intro-lab.svg'" :alt="$t('home.labAlt')" />
        </div>
        <div class="intro-content" data-aos="fade-left">
          <div class="section-label">{{ homeCfg('home_about_label') || $t('home.aboutLabel') }}</div>
          <h2 class="section-title-large">{{ homeCfg('home_about_title1') || $t('home.introTitleLine1') }}<br>{{ homeCfg('home_about_title2') || $t('home.introTitleLine2') }}</h2>
          <p class="intro-text">{{ homeCfg('home_about_desc') || $t('home.introText') }}</p>
          <div class="intro-stats">
            <div v-for="(s, i) in stats" :key="i" class="stat-item" data-aos="fade-up" :data-aos-delay="i * 100">
              <div class="stat-number">{{ locale === 'en-US' ? s.value : (s.value + (s.unit || '')) }}</div>
              <div class="stat-label">{{ locale === 'en-US' ? (s.nameEn || s.name) : s.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="media-showcase">
      <div class="container-large">
        <div class="media-grid">
          <div class="media-big" data-aos="fade-right">
            <img :src="config.home_intro_image || '/placeholder-images/intro-lab.svg'" :alt="$t('home.mediaBadge')" loading="lazy" />
            <div class="media-badge">
              <span class="badge-dot"></span>
              {{ $t('home.mediaBadge') }}
            </div>
          </div>
          <div class="media-side" data-aos="fade-left">
            <div class="video-card">
              <video
                v-if="homeVideo && !videoError"
                controls
                playsinline
                webkit-playsinline
                preload="metadata"
                @error="videoError = true"
              >
                <source :src="homeVideo" type="video/mp4">
              </video>
              <div v-else class="video-overlay">
                <span class="video-overlay-icon">🎬</span>
                <p>{{ $t('home.videoComingSoon') }}</p>
              </div>
            </div>
            <h3 class="media-title">{{ homeCfg('home_intro_title') || $t('home.intoTitle') }}</h3>
            <p class="media-desc">{{ homeCfg('home_intro_desc') || $t('home.intoDesc') }}</p>
            <button class="media-btn" @click="startInquiry">{{ $t('home.bookTour') }}</button>
          </div>
        </div>
      </div>
    </section>

    <section class="equipment-showcase">
      <div class="showcase-content">
        <div class="section-label-white" data-aos="fade-up">{{ $t('home.coreEquipment') }}</div>
        <h2 class="section-title-white" data-aos="fade-up" data-aos-delay="100">{{ $t('home.equipTitle') }}</h2>
        <div class="equipment-cards">
          <div
            v-for="(eq, i) in topEquipments"
            :key="eq.id"
            class="equipment-card-large"
            data-aos="zoom-in"
            :data-aos-delay="(i + 2) * 100"
            @click="goDetail('equipment', locale.value === 'en-US' ? (eq.nameEn || eq.name) : eq.name)"
          >
            <div class="card-image-large">
              <img :src="eq.coverImage || '/placeholder-images/equipment-1.svg'" :alt="eq.name" loading="lazy" />
            </div>
            <h3 class="card-title-large">{{ locale === 'en-US' ? (eq.nameEn || eq.name) : eq.name }}</h3>
            <p class="card-desc">{{ locale === 'en-US' ? (eq.descriptionEn || eq.description) : eq.description }}</p>
            <span class="card-btn-large">{{ $t('home.learnMoreArrow') }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="solutions-preview">
      <div class="container-large">
        <div class="section-label" data-aos="fade-up">{{ $t('home.solutions') }}</div>
        <h2 class="section-title-large" data-aos="fade-up" data-aos-delay="100">{{ $t('home.solutionsTitle') }}</h2>
        <div class="solutions-grid-large">
          <div
            v-for="(sol, i) in topSolutions"
            :key="sol.id"
            class="solution-card-hero"
            data-aos="fade-up"
            :data-aos-delay="(i + 2) * 100"
            @click="goDetail('solutions', locale.value === 'en-US' ? (sol.titleEn || sol.title) : sol.title)"
          >
            <div class="solution-card-media">
              <img :src="sol.coverImage || '/placeholder-images/solution-1.svg'" :alt="sol.title" class="solution-bg-image" loading="lazy" />
              <div class="solution-overlay-gradient"></div>
              <div class="solution-media-tag">{{ $t('home.solutions') }}</div>
            </div>
            <div class="solution-content-hero">
              <h3 class="solution-title-hero">{{ locale === 'en-US' ? (sol.titleEn || sol.title) : sol.title }}</h3>
              <p class="solution-desc-hero">{{ locale === 'en-US' ? (sol.summaryEn || sol.summary) : sol.summary }}</p>
              <span class="btn-solution-hero">{{ $t('home.viewCases') }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="cta-content" data-aos="fade-up">
        <h2 class="cta-title">{{ $t('home.ctaTitle') }}</h2>
        <p class="cta-desc">{{ $t('home.ctaDesc') }}</p>
        <div class="cta-buttons">
          <button class="btn-cta-primary" @click="startInquiry">{{ $t('home.quote') }}</button>
          <button class="btn-cta-secondary" @click="startInquiry">{{ $t('home.bookConsult') }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AOS from 'aos'
import { homeApi, systemConfigApi } from '../api'
import ErrorState from '../components/ErrorState.vue'
import { slugOf } from '../utils'
import { openInquiry } from '../utils/inquiry'

const DEFAULT_SLIDES = [
  {
    title: 'MegaMaker 巨型智造',
    titleEn: 'MegaMaker',
    subtitle: '金属增材制造 引领深海未来',
    subtitleEn: 'Metal Additive Manufacturing for the Deep-Sea Future',
    tagline: 'Metal Additive Manufacturing for Deep Ocean Engineering',
    gradient: '45deg, #0B4F8C 0%, #2196F3 100%'
  },
  {
    title: '突破传统制造边界',
    titleEn: 'Break Traditional Manufacturing Boundaries',
    subtitle: '成本降低50% 重量减轻70%',
    subtitleEn: '50% Cost Reduction · 70% Weight Reduction',
    tagline: 'Break Through Traditional Manufacturing Boundaries',
    gradient: '135deg, #1565C0 0%, #42A5F5 100%'
  },
  {
    title: '深海工程领航者',
    titleEn: 'Leader in Deep-Sea Engineering',
    subtitle: '6000米深度 钛合金技术',
    subtitleEn: '6000m Depth · Titanium Alloy Technology',
    tagline: 'Leader in Deep Ocean Engineering',
    gradient: '225deg, #0D47A1 0%, #1976D2 100%'
  }
]

const GRADIENTS = [
  '45deg, #0B4F8C 0%, #2196F3 100%',
  '135deg, #1565C0 0%, #42A5F5 100%',
  '225deg, #0D47A1 0%, #1976D2 100%'
]

export default {
  name: 'Home',
  components: { ErrorState },
  setup() {
    const router = useRouter()
    const { locale } = useI18n()
    const currentSlide = ref(0)
    const slides = ref([...DEFAULT_SLIDES])

    // 轮播链接：有配置时点击轮播内容跳转(内部=站内路由 / 外部=新窗口)
    const goSlide = (s) => {
      if (!s) return
      const link = s.link || ''
      const type = s.linkType
      if (type === 0 || !link) return
      if (type === 2 || /^https?:\/\//i.test(link)) {
        const url = /^https?:\/\//i.test(link) ? link : `https://${link}`
        window.open(url, '_blank', 'noopener')
      } else {
        router.push(link)
      }
    }

    // 轮播背景：有真实图片则「图片 + 深色渐变压暗」，占位图/无图回退纯渐变
    const slideBg = (slide) => {
      if (slide && slide.image && !slide.image.includes('/placeholder-')) {
        return `linear-gradient(135deg, rgba(8, 21, 39, 0.62), rgba(8, 21, 39, 0.16) 55%, rgba(8, 21, 39, 0.5)), url(${slide.image})`
      }
      return slide && slide.gradient
        ? `linear-gradient(${slide.gradient})`
        : 'linear-gradient(135deg, #0B4F8C, #2196F3)'
    }
    const stats = ref([])
    const equipments = ref([])
    const solutions = ref([])
    const videoError = ref(false)

    // 首页两板块（关于我们 / 走进巨型智造）图文视频来自后台配置；留空回退默认文案/占位
    const homeVideo = computed(() => (config.value && config.value.home_intro_video) || '')
    const homeCfg = (base) => {
      const lang = locale.value === 'en-US' ? 'en' : 'zh'
      return (config.value && config.value[`${base}_${lang}`]) || ''
    }
    const config = ref({})
    const loadError = ref(false)

    // 设备/方案：直接展示后台全部上架条目（后台有 3 条就展示 3 条，新增自动出现，不再按分类去重）
    const topEquipments = computed(() => equipments.value || [])
    const topSolutions = computed(() => solutions.value || [])

    const loadHome = async () => {
      try {
        const data = await homeApi()
        // 轮播：banner 数据驱动，渐变背景循环
        const banners = data.banners || []
        if (banners.length > 0) {
          slides.value = banners.map((b, i) => ({
            title: b.title,
            titleEn: b.titleEn || '',
            subtitle: b.subtitle || '',
            subtitleEn: b.subtitleEn || '',
            tagline: 'Metal Additive Manufacturing for Deep Ocean Engineering',
            gradient: GRADIENTS[i % GRADIENTS.length],
            image: b.image || '',
            linkType: b.linkType != null ? b.linkType : 0,
            link: b.linkUrl || ''
          }))
        }
        stats.value = data.stats || []
        equipments.value = data.equipments || []
        solutions.value = data.solutions || []
        // 数据渲染后重新触发滚动动画
        AOS.refresh()
      } catch (e) {
        // 加载失败显示报错页
        loadError.value = true
      }
    }

    const retryLoad = () => {
      loadError.value = false
      loadHome()
      loadConfig()
    }

    // 系统配置（首页素材图，后台"系统配置"可改）
    const loadConfig = async () => {
      try {
        const data = await systemConfigApi()
        config.value = data || {}
      } catch (e) {
        // 失败保持默认图
      }
    }

    const goDetail = (type, name) => {
      const slug = slugOf(name)
      if (slug) {
        router.push(`/${type}/${slug}`)
      }
    }

    const goAbout = () => {
      router.push('/about')
    }

    const startInquiry = () => {
      openInquiry()
    }

    let autoplayTimer = null

    const nextSlide = () => {
      currentSlide.value = (currentSlide.value + 1) % slides.value.length
    }

    const prevSlide = () => {
      currentSlide.value = (currentSlide.value - 1 + slides.value.length) % slides.value.length
      resetAutoplay() // 手动切换后重置自动播放
    }

    const goToSlide = (index) => {
      currentSlide.value = index
      resetAutoplay() // 手动切换后重置自动播放
    }

    const startAutoplay = () => {
      stopAutoplay()
      autoplayTimer = setInterval(() => {
        nextSlide()
      }, 5000) // 每5秒切换
    }

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer)
        autoplayTimer = null
      }
    }

    const resetAutoplay = () => {
      stopAutoplay()
      // 手动切换后等待8秒再开始自动播放
      setTimeout(() => {
        startAutoplay()
      }, 8000)
    }

    onMounted(() => {
      startAutoplay()
      loadHome()
      loadConfig()
    })

    onUnmounted(() => {
      stopAutoplay()
    })

    return {
      currentSlide,
      slides,
      slideBg,
      goSlide,
      stats,
      config,
      locale,
      loadError,
      retryLoad,
      topEquipments,
      topSolutions,
      videoError,
      homeVideo,
      homeCfg,
      goDetail,
      goAbout,
      startInquiry,
      nextSlide,
      prevSlide,
      goToSlide
    }
  }
}
</script>

<style scoped>
/* ===== 视频/图库展示区 ===== */
.media-showcase {
  padding: 80px 0;
  background: #F5F8FC;
}

.media-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 48px;
  align-items: center;
}

.media-big {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(11, 79, 140, 0.18);
}

.media-big img {
  width: 100%;
  display: block;
  transition: transform 0.6s ease;
}

.media-big:hover img {
  transform: scale(1.04);
}

.media-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border-radius: 40px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #0B4F8C;
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4CAF50;
  animation: dotPulse 1.5s ease infinite;
}

@keyframes dotPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.video-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 16px 40px rgba(11, 79, 140, 0.15);
}

.video-card video {
  width: 100%;
  display: block;
  aspect-ratio: 16 / 10;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(11, 26, 45, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.video-overlay-icon {
  font-size: 44px;
  margin-bottom: 12px;
  animation: floatIcon 2.5s ease-in-out infinite;
}

@keyframes floatIcon {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.video-overlay p {
  font-size: 15px;
  opacity: 0.9;
}

.media-title {
  font-size: 22px;
  color: #1A1A1A;
  margin-bottom: 8px;
}

.media-desc {
  font-size: 14px;
  color: #757575;
  line-height: 1.8;
  margin-bottom: 20px;
}

.media-btn {
  background: linear-gradient(90deg, #0B4F8C, #2196F3);
  color: #fff;
  border: none;
  border-radius: 40px;
  padding: 11px 32px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
}

.media-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(33, 150, 243, 0.35);
}

@media (max-width: 900px) {
  .media-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

/* 全屏Hero区 */
.hero-fullscreen {
  position: relative;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 轮播背景 */
.hero-carousel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transition: opacity 1s ease-in-out;
}

.carousel-slide.active {
  opacity: 1;
}

/* 轮播指示器 */
.carousel-indicators {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  gap: 12px;
}

.indicator-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  border: 2px solid rgba(255, 255, 255, 0.65);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.indicator-dot:hover {
  background: rgba(255, 255, 255, 0.75);
  transform: scale(1.2);
}

.indicator-dot.active {
  background: #FFFFFF;
  width: 32px;
  border-radius: 6px;
}

/* 轮播箭头 */
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  background: rgba(255, 255, 255, 0.14);
  border: 2px solid rgba(255, 255, 255, 0.35);
  color: #FFFFFF;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.carousel-arrow:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-50%) scale(1.1);
}

.carousel-prev {
  left: 40px;
}

.carousel-next {
  right: 40px;
}

/* 内容切换动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-content {
  text-align: center;
}

.hero-video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.gradient-animation {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    #0B4F8C 0%,
    #2196F3 25%,
    #64B5F6 50%,
    #2196F3 75%,
    #0B4F8C 100%
  );
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, rgba(9, 24, 43, 0.32) 0%, rgba(9, 24, 43, 0.14) 55%, rgba(9, 24, 43, 0.04) 100%);
  z-index: 2;
}

.hero-fullscreen.slide-linkable {
  cursor: pointer;
}

.hero-content-center {
  position: relative;
  z-index: 3;
  text-align: center;
  color: #FFFFFF;
  max-width: 1000px;
  padding: 0 40px;
}

.hero-title-large {
  font-size: 72px;
  font-weight: 800;
  margin-bottom: 24px;
  line-height: 1.2;
  color: #FFFFFF;
  text-shadow: 0 4px 28px rgba(0, 0, 0, 0.45), 0 1px 3px rgba(0, 0, 0, 0.4);
}

.hero-subtitle-large {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #FFFFFF;
  opacity: 0.95;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.35);
}

.hero-tagline {
  font-size: 18px;
  color: #FFFFFF;
  opacity: 0.9;
  margin-bottom: 48px;
  letter-spacing: 1px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
}

.hero-buttons-large {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.btn-primary-large {
  background: #FFFFFF;
  color: #0B4F8C;
  border: none;
  padding: 18px 48px;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
}

.btn-primary-large:hover {
  background: #0B4F8C;
  color: #FFFFFF;
  transform: translateY(-4px);
  box-shadow: 0 14px 36px rgba(11, 79, 140, 0.35);
}

.btn-secondary-large {
  background: transparent;
  color: #FFFFFF;
  border: 2px solid rgba(255, 255, 255, 0.95);
  padding: 16px 48px;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s ease;
}

.btn-secondary-large:hover {
  background: rgba(255, 255, 255, 0.92);
  color: #0B4F8C;
  transform: translateY(-4px);
}

.hero-scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #FFFFFF;
  font-size: 14px;
  cursor: pointer;
  display: none; /* 隐藏向下滚动指示器，避免与轮播指示器重复 */
}

.scroll-icon {
  width: 30px;
  height: 50px;
  border: 2px solid #FFFFFF;
  border-radius: 20px;
  position: relative;
}

.scroll-icon::before {
  content: '';
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  background: #FFFFFF;
  border-radius: 50%;
  animation: scroll-down 2s infinite;
}

@keyframes scroll-down {
  0%, 100% {
    top: 10px;
    opacity: 1;
  }
  50% {
    top: 30px;
    opacity: 0.5;
  }
}

/* 介绍区 */
.intro-section {
  padding: 120px 0;
  background: #FFFFFF;
}

.intro-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.intro-image img {
  width: 100%;
  height: 600px;
  object-fit: cover;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.section-label {
  display: inline-block;
  background: #E3F2FD;
  color: #0B4F8C;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
}

.section-title-large {
  font-size: 48px;
  font-weight: 800;
  color: #1A1A1A;
  line-height: 1.3;
  margin-bottom: 32px;
}

.intro-text {
  font-size: 18px;
  color: #757575;
  line-height: 1.8;
  margin-bottom: 48px;
}

.intro-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.stat-item {
  text-align: left;
}

.stat-number {
  font-size: 48px;
  font-weight: 800;
  color: #2196F3;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 16px;
  color: #757575;
}

/* 设备展示区（纯色背景） */
.equipment-showcase {
  position: relative;
  padding: 120px 0;
  overflow: hidden;
  background: #0B1A2D;
}

.showcase-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.showcase-bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(11, 26, 45, 0.92) 0%, rgba(11, 79, 140, 0.88) 100%);
}

.showcase-content {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  text-align: center;
}

.section-label-white {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  color: #FFFFFF;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  backdrop-filter: blur(10px);
}

.section-title-white {
  font-size: 48px;
  font-weight: 800;
  color: #FFFFFF;
  margin-bottom: 64px;
}

.equipment-cards {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 24px;
  padding: 20px 2px 40px; /* 给 hover 上移与阴影留空间,防被横滑容器裁切 */
}

.equipment-card-large {
  flex: 1 0 31%; /* 桌面 3 张铺满整行;超过 3 张时容器横向滑动查看 */
  scroll-snap-align: start;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  padding: 48px 32px;
  text-align: center;
  transition: all 0.4s ease;
  cursor: pointer;
}

.equipment-card-large:hover {
  transform: translateY(-16px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
}

.card-image-large {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
}

.card-image-large img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.card-title-large {
  font-size: 24px;
  font-weight: 700;
  color: #1A1A1A;
  margin-bottom: 16px;
}

.card-desc {
  font-size: 16px;
  color: #757575;
  line-height: 1.6;
  margin-bottom: 24px;
}

.card-btn-large {
  background: transparent;
  color: #2196F3;
  border: 2px solid #2196F3;
  padding: 12px 32px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.card-btn-large:hover {
  background: #2196F3;
  color: #FFFFFF;
}

/* 解决方案预览区 */
.solutions-preview {
  padding: 120px 0;
  background: #F5F5F5;
}

.container-large {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  text-align: center;
}

.solutions-grid-large {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 64px;
}

.solution-card-hero {
  position: relative;
  background: #FFFFFF;
  border-radius: 24px;
  padding: 14px 14px 30px;
  box-shadow: 0 8px 32px rgba(11, 79, 140, 0.1);
  cursor: pointer;
  transition: all 0.4s ease;
}

.solution-card-hero:hover {
  transform: translateY(-12px);
  box-shadow: 0 24px 64px rgba(11, 79, 140, 0.18);
}

.solution-card-media {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
}

.solution-bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.solution-card-hero:hover .solution-bg-image {
  transform: scale(1.08);
}

.solution-overlay-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, transparent 55%, rgba(11, 26, 45, 0.55) 100%);
}

.solution-media-tag {
  position: absolute;
  top: 14px;
  left: 14px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  border-radius: 40px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #0B4F8C;
}

.solution-content-hero {
  padding: 22px 8px 0;
  color: #1A1A1A;
  text-align: left;
}

.solution-title-hero {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #0B4F8C;
}

.solution-desc-hero {
  font-size: 14px;
  color: #757575;
  line-height: 1.7;
  margin-bottom: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.btn-solution-hero {
  display: inline-block;
  background: linear-gradient(90deg, #0B4F8C, #2196F3);
  color: #FFFFFF;
  border: none;
  padding: 10px 24px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-solution-hero:hover {
  transform: translateX(6px);
  box-shadow: 0 8px 20px rgba(33, 150, 243, 0.35);
}

/* CTA区 */
.cta-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #0B4F8C 0%, #2196F3 100%);
  text-align: center;
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 40px;
  color: #FFFFFF;
}

.cta-title {
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 24px;
}

.cta-desc {
  font-size: 20px;
  opacity: 0.9;
  margin-bottom: 48px;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.btn-cta-primary {
  background: #FFFFFF;
  color: #0B4F8C;
  border: none;
  padding: 18px 48px;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s ease;
}

.btn-cta-primary:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 48px rgba(255, 255, 255, 0.4);
}

.btn-cta-secondary {
  background: transparent;
  color: #FFFFFF;
  border: 3px solid #FFFFFF;
  padding: 15px 48px;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s ease;
}

.btn-cta-secondary:hover {
  background: #FFFFFF;
  color: #0B4F8C;
  transform: translateY(-4px);
}

/* ===== 移动端适配 ===== */
@media (max-width: 900px) {
  .hero-title-large {
    font-size: 40px;
  }
  .hero-subtitle-large {
    font-size: 22px;
  }
  .hero-content-center {
    padding: 0 24px;
  }
  .section-title-large {
    font-size: 32px;
  }
  .solutions-grid-large {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  .equipment-card-large {
    flex: 1 0 46%;
  }
  .intro-container {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 0 24px;
  }
  .intro-stats {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  .equipment-showcase,
  .cta-section {
    padding: 80px 0;
  }
  .cta-buttons {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .hero-title-large {
    font-size: 30px;
  }
  .hero-subtitle-large {
    font-size: 18px;
  }
  .hero-tagline {
    font-size: 12px;
  }
  .solutions-grid-large {
    grid-template-columns: 1fr;
  }
  .equipment-cards {
    gap: 16px;
    scrollbar-width: none;
  }
  .equipment-cards::-webkit-scrollbar {
    display: none;
  }
  .equipment-card-large {
    flex: 0 0 80%;
    padding: 28px 20px;
  }
  .card-image-large {
    height: 200px;
    margin-bottom: 20px;
  }
  .showcase-content {
    padding: 0 20px;
  }
  .section-title-large {
    font-size: 26px;
  }
  .equipment-showcase,
  .cta-section {
    padding: 64px 0;
  }
}

/* ===== 平板横屏（901-1024px） ===== */
@media (max-width: 1024px) and (min-width: 901px) {
  .hero-title-large {
    font-size: 48px;
  }
  .hero-subtitle-large {
    font-size: 26px;
  }
  .section-title-large {
    font-size: 38px;
  }
  .solutions-grid-large {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
  .equipment-card-large {
    flex: 1 0 46%;
  }
}

/* ===== 手机中间档（481-767px） ===== */
@media (max-width: 767px) and (min-width: 481px) {
  .section-title-large {
    font-size: 30px;
  }
}

/* ===== 手机触控优化 ===== */
@media (max-width: 767px) {
  .carousel-arrow {
    width: 44px;
    height: 44px;
  }
  .carousel-prev {
    left: 8px;
  }
  .carousel-next {
    right: 8px;
  }
  .carousel-indicators {
    bottom: 18px;
  }
}
/* ===== 移动端:首页(关于我们大图/数据/核心设备与解决方案标题) ===== */
@media (max-width: 900px) {
  .section-title-white {
    font-size: 34px;
    margin-bottom: 40px;
  }
  .intro-image img {
    height: 360px;
  }
}
@media (max-width: 767px) {
  .intro-image img {
    height: 260px;
  }
  .section-title-white {
    font-size: 30px;
    margin-bottom: 32px;
  }
  .intro-stats {
    gap: 12px;
  }
  .stat-number {
    font-size: 34px;
    margin-bottom: 4px;
  }
}
@media (max-width: 480px) {
  .intro-container {
    gap: 32px;
  }
  .intro-image img {
    height: 220px;
  }
  .stat-number {
    font-size: 28px;
  }
  .stat-label {
    font-size: 13px;
  }
}
</style>